// The one place that talks to the Measure2Quote API.
import { createApiClient, feedbackSender } from '@higgins/utils';
import type { DeviceTable, Fields } from './device-db';
import { getAccessToken } from './msal';
import type { JobFromApi, JobSearchHit, Me, PaintProductFromApi, PricingReferenceFromApi, RecentJob, ReferenceFromApi, SavedFromApi } from './types';
import type { SupplierQuote } from '@higgins/m2q-pricing';

// No connection, as opposed to the API saying no: sync treats it as "try again later", never as a failed change.
export { ApiError, isConnectionFailure } from '@higgins/utils';

// Headers, ApiError and the lost-reply handling come from the shared client (#312).
// getAccessToken refuses with OfflineError while the device is offline, before any fetch.
const { json: apiFetch, request: apiRequest } = createApiClient({ getToken: getAccessToken });

export const getMe = () => apiFetch<Me>('/me');

/** POST /api/feedback — the header's Report a problem / Suggest button (@higgins/ui FeedbackButton). */
export const sendFeedback = feedbackSender(apiFetch);

export const getReference = () => apiFetch<ReferenceFromApi>('/reference');

export const getPricingReference = (jobId: string, date: string) =>
  apiFetch<PricingReferenceFromApi>(`/pricing-reference?${new URLSearchParams({ job: jobId, date })}`);

export const searchPaints = (search: string, date: string) =>
  apiFetch<{ products: PaintProductFromApi[]; more: boolean }>(`/paint-products?${new URLSearchParams({ search, date })}`);

export const resolvePaints = (ids: string[], date: string) =>
  apiFetch<{ products: Record<string, PaintProductFromApi | null> }>(`/paint-products/resolve?${new URLSearchParams({ ids: ids.join(','), date })}`);

/** POST /api/options/{id}/accept — what the server re-priced and wrote onto the opportunity. */
export interface AcceptedFromApi {
  done: true;
  lines: { productNumber: string; sell: number; cost: number; hoursLitres: number | null }[];
  totals: { sell: number; cost: number; gpAmount: number; gpPercent: number };
  crmSell: number;
  crmCost: number | null;
  replacedManualLines: number;
}

/** A round of a big Accept that ran out of time on the server: `left` line writes still to do. */
interface AcceptRoundFromApi {
  done: false;
  left: number;
  replacedManualLines: number;
}

/** Rounds a big Accept may take (72 lines at ~1.5 s each fit in 5); more means it isn't getting anywhere. */
const ACCEPT_ROUNDS = 10;
/** Each option's Accept id while its Accept hasn't finished on this screen. */
const unfinishedAccepts = new Map<string, string>();

/**
 * Accept, in as many rounds as the server needs: each call writes what fits in its time and says how many
 * line writes are left; the next carries on from what's already there (acceptance-write.ts). `onProgress`
 * hears the count after each unfinished round.
 */
export async function acceptOptionApi(optionId: string, replaceManualLines: boolean, onProgress?: (left: number) => void): Promise<AcceptedFromApi> {
  let replaced = 0;
  // One id for this Accept, kept until it finishes: accepting again after a failure carries on as the same
  // Accept, so the server's claim on the job (#545) is its own and not refused as someone else's.
  const acceptId = unfinishedAccepts.get(optionId) ?? crypto.randomUUID();
  unfinishedAccepts.set(optionId, acceptId);
  for (let round = 0; round < ACCEPT_ROUNDS; round++) {
    const answer = await apiFetch<AcceptedFromApi | AcceptRoundFromApi>(`/options/${optionId}/accept`, { method: 'POST', body: JSON.stringify({ replaceManualLines, acceptId }) });
    replaced = Math.max(replaced, answer.replacedManualLines);
    if (answer.done) {
      unfinishedAccepts.delete(optionId);
      return { ...answer, replacedManualLines: replaced };
    }
    onProgress?.(answer.left);
  }
  throw new Error('Accepting took too many rounds and stopped part-way.');
}

/** POST /api/versions/{id}/issue — the version goes to the client and is frozen (DD, 2026-10-22). */
export const issueVersionApi = (versionId: string) => apiFetch<{ etag: string }>(`/versions/${versionId}/issue`, { method: 'POST' });

/** One Keep / Use book choice on the reprice check, for one price on a job price row. The server looks up today's book price. */
export interface RepriceDecisionToApi {
  jobPriceId: string;
  part: 'price' | 'dayRate' | 'transport';
  decision: 'keep' | 'book';
}

/** POST /api/versions/{id}/new-version — version n+1 at today's rates; the old one is frozen. */
export const newVersionApi = (versionId: string, decisions: RepriceDecisionToApi[]) =>
  apiFetch<{ versionId: string }>(`/versions/${versionId}/new-version`, { method: 'POST', body: JSON.stringify({ decisions }) });

/** POST /api/areas/{id}/remove — retire an area in CRM (needs the device's etag; refused while an option is accepted). */
export const removeAreaApi = (areaId: string, etag: string) =>
  apiFetch<{ removed: string; measureId?: string; measureEtag?: string }>(`/areas/${areaId}/remove`, { method: 'POST', body: JSON.stringify({ etag }) });

/** POST /api/buildings/{id}/remove — an empty building retired in CRM; `areaEtags` for areas whose photo folders lost the building's name. */
export const removeBuildingApi = (buildingId: string, etag: string) =>
  apiFetch<{ removed: string; areaEtags?: { id: string; etag: string }[] }>(`/buildings/${buildingId}/remove`, { method: 'POST', body: JSON.stringify({ etag }) });

export const removeOptionApi = (optionId: string) => apiFetch<{ removed: string }>(`/options/${optionId}`, { method: 'DELETE' });

/** POST /api/options/{id}/unlock — an accepted option made changeable again (refused once the opportunity is won or lost). */
export const unlockOptionApi = (optionId: string) => apiFetch<{ etag: string }>(`/options/${optionId}/unlock`, { method: 'POST' });

export const getJobByNumber = (number: string) =>
  apiFetch<JobFromApi>(`/jobs?${new URLSearchParams({ number })}`);

/** Jobs whose number starts with `q` (#392), for Open a job. */
/** The jobs the user opened lately, on any device, newest first (#422). */
export const getRecentJobs = () => apiFetch<{ jobs: RecentJob[] }>('/recent-jobs');

/** Note that the user opened a job (sent by the outbox). */
export const saveRecentJobToApi = (jobId: string, openedAt: string, signal?: AbortSignal) =>
  apiFetch<{ saved: boolean }>(`/recent-jobs/${jobId}`, { method: 'PUT', body: JSON.stringify({ openedAt }), signal });

export const searchJobs = (q: string) => apiFetch<{ jobs: JobSearchHit[] }>(`/jobs/search?${new URLSearchParams({ q })}`);

/** By opportunity id — an "Open in Measure2Quote" link from CRM. */
export const getJobById = (id: string, signal?: AbortSignal) => apiFetch<JobFromApi>(`/jobs?${new URLSearchParams({ id })}`, { signal });

export const saveRecordToApi = (
  table: DeviceTable,
  id: string,
  body: { fields: Fields; parentId?: string; etag?: string; ratingScale?: string; scaleFrom?: string },
  signal?: AbortSignal,
) => apiFetch<SavedFromApi>(`/records/${table}/${id}`, { method: 'PUT', body: JSON.stringify(body), signal });

/** The JPEG goes as the raw body; the shared client keeps this Content-Type. */
/** A photo already on the job's SharePoint site (the device keeps only its thumbnail). */
export async function getPhotoContent(areaId: string, photoId: string, size?: 'thumbnail'): Promise<Blob> {
  const res = await apiRequest(`/photos/content?${new URLSearchParams({ areaId, photoId, ...(size ? { size } : {}) })}`);
  return res.blob();
}

/** The proposal for an option (POST /api/proposals): the .docx, its name, and where it was saved or why it wasn't. */
export interface ProposalFromApi {
  fileName: string;
  blob: Blob;
  savedUrl: string | null;
  saveProblem: string | null;
}

const header = (res: Response, name: string) => {
  const value = res.headers.get(name);
  return value ? decodeURIComponent(value) : null;
};

export async function makeProposalApi(optionId: string, date: string): Promise<ProposalFromApi> {
  const res = await apiRequest('/proposals', { method: 'POST', body: JSON.stringify({ optionId, date }), headers: { 'Content-Type': 'application/json' } });
  return { fileName: header(res, 'X-File-Name') ?? 'Proposal.docx', blob: await res.blob(), savedUrl: header(res, 'X-Saved-Url'), saveProblem: header(res, 'X-Save-Problem') };
}

/** POST /api/takeoff-export (admins): the option in the STM002 take-off, saved to the job's site as one or more workbooks (#516). */
export interface TakeoffExportFromApi {
  template: string;
  files: { fileName: string; webUrl: string }[];
}

export const exportTakeoffApi = (optionId: string, date: string) =>
  apiFetch<TakeoffExportFromApi>('/takeoff-export', { method: 'POST', body: JSON.stringify({ optionId, date }) });

/** POST /api/review-packs (while the app is in UAT): the option's Take Off, Summary and REVIEW as one PDF on the job's site (#719). */
export const makeReviewPackApi = (optionId: string, date: string) =>
  apiFetch<{ fileName: string; webUrl: string; draftLeft?: { name: string } | null }>('/review-packs', { method: 'POST', body: JSON.stringify({ optionId, date }) });

/** POST /api/condition-reports: the visit's condition report, saved to the job's site as its next version (#513). */
export interface ConditionReportFromApi {
  fileName: string;
  webUrl: string;
  version: number;
  /** Which template it was made from: "Templafy's Condition Report", or the client's own. */
  template: string;
}

export type ReportSides = 'External' | 'Internal' | 'Both';

export const makeConditionReportApi = (visitId: string, sides: ReportSides, date: string) =>
  apiFetch<ConditionReportFromApi>('/condition-reports', { method: 'POST', body: JSON.stringify({ visitId, sides, date }) });

/** A visit's condition reports made so far, newest first. */
export interface IssuedReportFromApi {
  id: string;
  name: string;
  version: number | null;
  issuedOn: string | null;
  issuedBy: string | null;
  url: string | null;
  /** When it was marked sent to the client; null until then. */
  sentOn: string | null;
  pdfUrl: string | null;
}

/** POST /api/condition-reports/{id}/sent — the report went to the client; its visit becomes Issued (409 NOT_VALIDATED until validated). */
export const markReportSentApi = (reportId: string) =>
  apiFetch<{ sent: true; visitId: string; visit: VisitChangeFromApi }>(`/condition-reports/${reportId}/sent`, { method: 'POST' });

/** POST /api/condition-reports/{id}/pdf — a PDF copy beside the Word report on the job site. */
export const makeReportPdfApi = (reportId: string) => apiFetch<{ webUrl: string }>(`/condition-reports/${reportId}/pdf`, { method: 'POST' });

/** POST /api/visits/{id}/signature — a signature (PNG) for the visit; the client's names who signed. */
export const signVisitApi = (visitId: string, who: 'client' | 'technician', name: string, png: Blob) =>
  apiFetch<VisitChangeFromApi>(`/visits/${visitId}/signature?${new URLSearchParams({ who, name })}`, { method: 'POST', body: png, headers: { 'Content-Type': 'image/png' } });

export const listConditionReportsApi = (visitId: string) => apiFetch<{ reports: IssuedReportFromApi[] }>(`/condition-reports?${new URLSearchParams({ visitId })}`);

/** Save a generated PDF to the job's SharePoint site and list it in CRM (POST /api/documents). */
export const saveDocumentToApi = (versionId: string, fileName: string, pdf: Blob) =>
  apiFetch<{ webUrl: string }>(`/documents?${new URLSearchParams({ versionId, fileName })}`, { method: 'POST', body: pdf, headers: { 'Content-Type': 'application/pdf' } });

/** Save a supplier's quote (PDF) for one of the option's Take Off lines to the job site's Quotes folder (POST /api/supplier-quotes, #654). */
export const uploadSupplierQuoteApi = (optionId: string, fileName: string, pdf: Blob) =>
  apiFetch<SupplierQuote>(`/supplier-quotes?${new URLSearchParams({ optionId, fileName })}`, { method: 'POST', body: pdf, headers: { 'Content-Type': 'application/pdf' } });

export function uploadPhotoToApi(photo: {
  areaId: string;
  photoId: string;
  surfaceId: string | null;
  areaPhoto: boolean;
  fileName: string;
  image: Blob;
  direction: string | null;
  bearing: number | null;
  position: { latitude: number; longitude: number; accuracy: number } | null;
}, signal?: AbortSignal) {
  const query = new URLSearchParams({ areaId: photo.areaId, photoId: photo.photoId, fileName: photo.fileName });
  if (photo.surfaceId) query.set('surfaceId', photo.surfaceId);
  if (photo.areaPhoto) query.set('areaPhoto', '1');
  if (photo.direction) query.set('direction', photo.direction);
  if (photo.bearing !== null) query.set('bearing', String(photo.bearing));
  if (photo.position) {
    query.set('latitude', String(photo.position.latitude));
    query.set('longitude', String(photo.position.longitude));
    query.set('accuracy', String(photo.position.accuracy));
  }
  return apiFetch<SavedFromApi>(`/photos?${query}`, { method: 'POST', body: photo.image, headers: { 'Content-Type': 'image/jpeg' }, signal });
}

/** GET /api/nearmap/tile/{z}/{x}/{y} — one 256 px aerial tile, through the API (the Nearmap key stays on the server). */
export async function getNearmapTile(z: number, x: number, y: number): Promise<Blob> {
  return (await apiRequest(`/nearmap/tile/${z}/${x}/${y}`)).blob();
}

/**
 * POST /api/site-plan — save a visit's plan to the job's SharePoint site; with
 * `copy`, one of its copies (marked: pins drawn on; access: access lines drawn on) of the plan saved at `planSavedOn`.
 */
export const uploadSitePlanApi = (visitId: string, image: Blob, copy?: { copy: 'marked' | 'access'; planSavedOn: string | undefined }) =>
  apiFetch<{ driveItemId: string; webUrl: string }>(`/site-plan?${new URLSearchParams({ visitId, ...(copy ? { copy: copy.copy, ...(copy.planSavedOn ? { planSavedOn: copy.planSavedOn } : {}) } : {}) })}`, { method: 'POST', body: image, headers: { 'Content-Type': 'image/jpeg' } });

/** GET /api/site-plan/content — the visit's plan, for a device that didn't make it. */
export async function getSitePlanContent(visitId: string): Promise<Blob> {
  return (await apiRequest(`/site-plan/content?${new URLSearchParams({ visitId })}`)).blob();
}

/** POST /api/photos/{id}/remove — the photo's row made inactive, its file to SharePoint's recycle bin. */
export const removePhotoApi = (photoId: string, signal?: AbortSignal) => apiFetch<{ removed: string }>(`/photos/${photoId}/remove`, { method: 'POST', signal });

/** PATCH /api/photos/{id}/direction — an uploaded photo's direction, on its area's list. */
export const savePhotoDirectionToApi = (areaId: string, photoId: string, direction: string | null, signal?: AbortSignal) =>
  apiFetch<SavedFromApi>(`/photos/${photoId}/direction?${new URLSearchParams({ areaId })}`, { method: 'PATCH', body: JSON.stringify({ direction }), signal });

/** A visit's columns the server changed, as the device holds them, with the visit's new etag. */
export interface VisitChangeFromApi {
  etag: string;
  fields: Fields;
}

/** POST /api/visits/{id}/status — complete, validate or reopen a condition visit; 409 UNRATED_AREAS { areas } unless `force`. Validating writes each building's condition. */
export const changeVisitStatusApi = (visitId: string, change: 'complete' | 'validate' | 'reopen', force = false) =>
  apiFetch<VisitChangeFromApi & { buildings?: (VisitChangeFromApi & { id: string })[] }>(`/visits/${visitId}/status`, { method: 'POST', body: JSON.stringify({ change, ...(force ? { force } : {}) }) });

export interface CentreFromApi {
  id: string;
  name: string;
  address: string;
}

export interface PastVisitFromApi {
  id: string;
  name: string;
  inspectedOn: string | null;
  jobNumber: string;
  status: string | null;
}

/** GET /api/visits/{id}/links — the visit's centre, the client's centres, and earlier visits to its centre. */
export const getVisitLinksApi = (visitId: string) =>
  apiFetch<{ centreId: string | null; centres: CentreFromApi[]; previousVisitId: string | null; pastVisits: PastVisitFromApi[]; yearBuilt: number | null }>(`/visits/${visitId}/links`);

/** A change to a visit's links: its centre, the visit before it (null clears), the centre's year built (null clears). */
export interface VisitLinksToApi {
  facilityId?: string;
  previousVisitId?: string | null;
  yearBuilt?: number | null;
}

/** POST /api/visits/{id}/links — move the visit to another of the client's centres, set the visit before it, or the year built. */
export const saveVisitLinksApi = (visitId: string, change: VisitLinksToApi) =>
  apiFetch<VisitChangeFromApi>(`/visits/${visitId}/links`, { method: 'POST', body: JSON.stringify(change) });

export interface ClientContactFromApi {
  id: string;
  name: string;
  jobTitle: string;
  email: string;
}

/** GET /api/visits/{id}/contact — who the condition report goes to, and the client's contacts. */
export const getReportContactsApi = (visitId: string) =>
  apiFetch<{ contactId: string | null; chosen: boolean; jobContactId: string | null; contacts: ClientContactFromApi[] }>(`/visits/${visitId}/contact`);

/** POST /api/visits/{id}/contact — address the report to one of the client's contacts (null: the job's). */
export const chooseReportContactApi = (visitId: string, contactId: string | null) =>
  apiFetch<VisitChangeFromApi>(`/visits/${visitId}/contact`, { method: 'POST', body: JSON.stringify({ contactId }) });

export interface NewContactToApi {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
}

/** POST /api/visits/{id}/contacts — a new contact on the client's account, the report addressed to it; 409 CONTACT_EXISTS { contact }. */
export const addReportContactApi = (visitId: string, contact: NewContactToApi) =>
  apiFetch<VisitChangeFromApi & { contact: ClientContactFromApi }>(`/visits/${visitId}/contacts`, { method: 'POST', body: JSON.stringify(contact) });

/** POST /api/options/{id}/register — the won job's buildings and areas into Field Service's register; call again until done. */
export const registerJobApi = (optionId: string) =>
  apiFetch<{ done: boolean; changes: (VisitChangeFromApi & { id: string })[] }>(`/options/${optionId}/register`, { method: 'POST' });

/** An evidence point in the visit's centre's register (GET /api/visits/{id}/evidence-points). */
export interface EvidencePointFromApi {
  id: string;
  name: string;
  registerAreaId: string;
  areaName: string;
  buildingName: string;
  latitude: number | null;
  longitude: number | null;
  bearing: number | null;
  accuracy: number | null;
}

export const listEvidencePointsApi = (visitId: string) => apiFetch<{ points: EvidencePointFromApi[] }>(`/visits/${visitId}/evidence-points`);

/** POST /api/photos/{id}/evidence-point — make an evidence point from an uploaded photo whose area is in the register. */
export const makeEvidencePointApi = (photoId: string) => apiFetch<{ pointId: string; etag: string }>(`/photos/${photoId}/evidence-point`, { method: 'POST' });

/** The point's reference photo, for the retake overlay. */
export async function getEvidenceReference(visitId: string, pointId: string): Promise<Blob> {
  return (await apiRequest(`/visits/${visitId}/evidence-points/${pointId}/reference`)).blob();
}
